import { createSlice, PayloadAction } from '@reduxjs/toolkit';

export interface CalculatorFormItem {
	data: any;
	page: string;
}

export interface CalculatorFormState {
	[key: string]: CalculatorFormItem;
}

const initialState: CalculatorFormState = {};

// Danish/US numeric shape with optional "kr"/"kr." suffix, e.g. "68.247 kr.", "1.500.000 kr",
// "1.234,50 kr.", "150,000", "150.000", "1 500 000" — digits plus ., "," or whitespace separators
// (\s covers NBSP), optional leading sign, case-insensitive suffix. Free text like
// "op til 500 kr. i selvrisiko" or "2026-01-01" does NOT match because non-numeric characters
// appear outside the suffix. Input must be trimmed first to keep the regex backtracking-free.
const CURRENCY_REGEX = /^-?[\d.,\s]+(?:kr\.?)?$/i;

// CMS staticFields authors amounts as pre-formatted display strings; strip to digits only when we
// recognize a Danish currency shape so identifiers/dates/free text pass through untouched.
const sanitizeStaticFieldValue = (raw: unknown): unknown => {
	if (typeof raw !== 'string') return raw;
	const trimmed = raw.trim();
	if (!CURRENCY_REGEX.test(trimmed)) return raw;
	const digits = trimmed.replace(/\D/g, '');
	if (!digits) return raw;
	return trimmed.startsWith('-') ? `-${digits}` : digits;
};

export const calculatorFormSlice = createSlice({
	name: 'CalculatorFormSlice',
	initialState,
	reducers: {
		addFormItem: (state, action: PayloadAction<{ key: string; item: CalculatorFormItem }>) => {
			const { key, item } = action.payload;
			return {
				...state,
				[key]: item,
			};
		},
		resetFormItem: (state, action: PayloadAction<{ key: string }>) => {
			const { key } = action.payload;
			return {
				...state,
				[key]: undefined,
			};
		},
		// Seeds metaArea.staticFields into the form slice without clobbering existing values,
		// so any component can read them via state.calculatorForm[uid].
		loadStaticFieldsToForm: (state, action: PayloadAction<MetaArea>) => {
			const staticFieldItems = action.payload?.items?.[0]?.content?.staticFields?.items;
			if (!staticFieldItems?.length) {
				return state;
			}

			const next: CalculatorFormState = { ...state };
			for (const item of staticFieldItems) {
				const content = item?.content;
				const key = content?.uniqueIdentifier?.uid;
				if (!key || next[key]?.data) continue;
				next[key] = {
					data: {
						id: key,
						value: sanitizeStaticFieldValue(content?.defaultValue),
						type: content?.contentType,
					},
					page: '0',
				};
			}
			return next;
		},
	},
});

export const { addFormItem, resetFormItem, loadStaticFieldsToForm } = calculatorFormSlice.actions;

export default calculatorFormSlice.reducer;
